import {addDays, addMonths, format, getYear, isAfter, isBefore, isSameDay, subDays, subYears} from 'date-fns'
import {keys} from 'lodash'
import * as z from 'zod/mini'
import {ALL_BODYWORKS} from '../../constants/bodyworks'
import colors from '../../constants/colors'
import {ALL_EXTENDED_FUELS, ALL_FUELS} from '../../constants/fuels'
import {ALL_INSURANCES} from '../../constants/insurances'
import {ALL_LEGAL_FORMS} from '../../constants/legalFormTypes'
import {ALL_PAYMENT_FREQUENCIES} from '../../constants/paymentFrequencies'
import {ALL_TELEPHONE_CODES} from '../../constants/telephoneCodes'
import {ALL_TRANSMISSIONS} from '../../constants/transmissions'
import {ALL_VEHICLE_IDENTIFIERS} from '../../constants/vehicleIdentifiers'
import * as vehicleTypes from '../../constants/vehicleTypes'
import {ALL_VEHICLE_USAGES, SUPPORTED_VEHICLE_USAGES} from '../../constants/vehicleUsages'
import {DEFAULT_BEGIN_DATE_MAX_MONTHS, getBirthdayFromBornNumber, validateBornNumber} from '../utils'
import {boolean, createEnum, nonNegativeInteger, positiveInteger, standardString} from './types'


const MIN_PRODUCTION_YEAR = 1975

// General

export const email = () => z.email().register(z.globalRegistry, {example: 'example@example.com'})

export const telephoneCode = () => createEnum(ALL_TELEPHONE_CODES)

export const telephoneNumber = () => z.string().check(z.regex(/^[1-9]\d{4,10}$/))
  .register(z.globalRegistry, {example: '903123456'})

export const telephoneWithoutPrefix = () => z.string().check(z.regex(/^(?!421)[1-9]\d{8}$/))
  .register(z.globalRegistry, {example: '903123456'})

export const telephoneWithPrefix = () => z.string().check(z.regex(/^\+[1-9]{1,5}[1-9]\d{4,10}$/))
  .register(z.globalRegistry, {example: '+421903123456'})

export const date = () => z.string().check(z.trim(), z.regex(/^\d{4}-(0[1-9]|1[0-2])-([0][1-9]|[12][0-9]|[3][01])$/))
  .register(z.globalRegistry, {example: '1980-03-24'})

const ibanChecksum = (iban: string) => {
  // Move the first four characters to the end
  const rearrangedIban = `${iban.slice(4)}${iban.slice(0, 4)}`

  // Convert letters to numbers (A = 10, B = 11, ..., Z = 35)
  const numericIban = BigInt(rearrangedIban.replace(/[A-Z]/g, (char) => String(char.charCodeAt(0) - 55)))

  return numericIban % 97n === 1n
}

export const iban = () => z.pipe(
  z.pipe(z.string(), z.transform((val) => val.toUpperCase().replace(/\s/g, ''))),
  z.string().check(z.regex(/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/), z.refine(ibanChecksum, {error: 'Neplatný IBAN'}))
)

export const contact = z.object({
  email: email(),
  message: z.string().check(z.minLength(1)),
})
export type Contact = z.infer<typeof contact>

// Vehicle

export const fuel = () => createEnum(ALL_FUELS)

export const extendedFuel = () => createEnum(ALL_EXTENDED_FUELS)

export const color = () => createEnum(keys(colors))

export const transmission = () => createEnum(ALL_TRANSMISSIONS)

export const bodywork = () => createEnum(ALL_BODYWORKS)

export const vehicleType = () => createEnum(vehicleTypes.SUPPORTED_VEHICLE_TYPES)

export const extendedVehicleType = () => createEnum(vehicleTypes.ALL_VEHICLE_TYPES)

export const vehicleTypeCar = () => createEnum(vehicleTypes.CAR_VEHICLE_TYPES)

export const extendedVehicleTypeCar = () => createEnum([
  vehicleTypes.VEHICLE_TYPE_CAR_M1,
  vehicleTypes.VEHICLE_TYPE_CAR_N1,
  vehicleTypes.VEHICLE_TYPE_MOTORCYCLE,
  vehicleTypes.VEHICLE_TYPE_TRAILER,
])

export const vehicleUsage = () => createEnum(SUPPORTED_VEHICLE_USAGES)

export const extendedVehicleUsage = () => createEnum(ALL_VEHICLE_USAGES)

export const licensePlate = () => z.pipe(
  z.pipe(z.string(), z.transform((val) => val.toUpperCase().replace(' ', ''))),
  z.string().check(z.length(7), z.regex(/^[A-Z0-9]+$/))
).register(z.globalRegistry, {example: 'BA111EX'})

export const vin = () => z.pipe(
  z.pipe(z.string(), z.transform((val) => val.toUpperCase())),
  z.string().check(z.length(17), z.regex(/^[A-HJ-NPR-Z0-9]{17}$/))
).register(z.globalRegistry, {example: 'AAAAAAAAAA0000000'})

export const scooterSerialNumber = () => z.pipe(
  z.pipe(z.string(), z.transform((val) => val.toUpperCase())),
  z.string().check(z.minLength(8), z.maxLength(22), z.regex(/^[A-Z0-9]+$/))
).register(z.globalRegistry, {example: '00123456'})

export const registrationDate = () => date().check(
  z.refine(
    (date) => isBefore(new Date(date), new Date()) || isSameDay(new Date(date), new Date()),
    {error: 'Dátum registrácie vozidla môže byť maximálne dnešný'},
  ),
  z.refine(
    (date) => isAfter(new Date(date), new Date(MIN_PRODUCTION_YEAR, 0)),
    {error: `Rok registrácie vozidla môže byť minimálne ${MIN_PRODUCTION_YEAR}`},
  )
).register(z.globalRegistry, {example: '2023-03-24'})

export const productionDate = () => date().check(
  z.refine(
    (date) => isBefore(new Date(date), new Date()) || isSameDay(new Date(date), new Date()),
    {error: 'Dátum výroby vozidla môže byť maximálne dnešný'},
  ),
  z.refine(
    (date) => isAfter(new Date(date), new Date(MIN_PRODUCTION_YEAR, 0)),
    {error: `Rok výroby vozidla môže byť minimálne ${MIN_PRODUCTION_YEAR}`},
  )
).register(z.globalRegistry, {example: '2023-03-24'})

export const registrationNumber = () => z.string().check(z.length(8), z.regex(/^[A-Z]{2}[A-Z0-9][0-9]{5}$/))
  .register(z.globalRegistry, {example: 'AB123456'})

export const vehicleIdentificationSome = z.union([
  z.object({
    licensePlate: licensePlate(),
    vin: z.optional(vin()),
  }),
  z.object({
    licensePlate: z.optional(licensePlate()),
    vin: vin(),
  }),
])
export type VehicleIdentificationSome = z.infer<typeof vehicleIdentificationSome>

export const isVehicleIdentificationSome = (
  vehicleIdentification: Record<string, unknown>
): vehicleIdentification is VehicleIdentificationSome => {
  return Boolean(vehicleIdentification.vin || vehicleIdentification.licensePlate)
}

export const vehicleIdentificationOptional = z.object({
  licensePlate: z.optional(licensePlate()),
  vin: z.optional(vin()),
})
export type VehicleIdentificationOptional = z.infer<typeof vehicleIdentificationOptional>

export const vehicle = {
  type: vehicleType(),
  brand: standardString('bmw'),
  model: standardString('rad5'),
  weight: positiveInteger({max: 3500, example: 2320}),
  volume: nonNegativeInteger({max: 10000, example: 1926}),
  power: positiveInteger({max: 1000, example: 159}),
  fuel: fuel(),
  seats: positiveInteger({max: 9, example: 5}),
  productionDate: productionDate(),
  productionYear: positiveInteger({max: getYear(new Date()), min: MIN_PRODUCTION_YEAR, example: 2021}),
  registrationDate: registrationDate(),
  registrationNumber: registrationNumber(),
  usage: vehicleUsage(),
  color: color(),
  odometer: positiveInteger({max: 10000000, example: 10000}),
  transmission: transmission(),
  bodywork: bodywork(),
}

// Person

export const legalForm = () => createEnum(ALL_LEGAL_FORMS)

export const companyNumber = () => z.string().check(
  z.trim(),
  z.minLength(6),
  z.maxLength(12),
  z.regex(/^[0-9]+$/),
  z.refine(
    (value) => value.length !== 6 && value.length !== 10,
    {error: 'Ak máte 6 mieste IČO, doplňte na začiatok dve nuly "00"'},
  ),
  z.refine(
    (value) => value.length === 8 || value.length === 12,
    {error: 'Neplatné IČO'},
  ),
).register(z.globalRegistry, {example: '54370833'})

const isAtLeast18YearsOld = (date: string) => {
  const eighteenYearsAgo = getYear(subYears(new Date(), 18))
  const birthdayYear = getYear(new Date(date))
  return eighteenYearsAgo - birthdayYear >= 0
}

const isBornAtLeastIn1900 = (date: string) => {
  const nineteenHundred = new Date('1900-01-01')
  const birthday = new Date(date)
  return isAfter(birthday, nineteenHundred) || isSameDay(birthday, nineteenHundred)
}

export const bornNumber = () => z.string().check(
  z.trim(),
  z.regex(/^(\d{2})([05][1-9]|[16][0-2])([0][1-9]|[12][0-9]|[3][01])(\d{3,4})$/),
  z.refine(validateBornNumber, {error: 'Neplatné rodné číslo'}),
  z.refine(
    (bornNumber) => isAtLeast18YearsOld(format(getBirthdayFromBornNumber(bornNumber), 'yyyy-MM-dd')),
    {error: 'Osoba musí mať aspoň 18 rokov'}
  ),
  z.refine(
    (bornNumber) => isBornAtLeastIn1900(format(getBirthdayFromBornNumber(bornNumber), 'yyyy-MM-dd')),
    {error: 'Rok narodenia musí byť vyšší ako 1900'}
  ),
).register(z.globalRegistry, {example: '9801209990'})

export const birthday = () => date().check(
  z.refine(isAtLeast18YearsOld, {error: 'Osoba musí mať aspoň 18 rokov'}),
  z.refine(isBornAtLeastIn1900, {error: 'Rok narodenia musí byť vyšší ako 1900'}),
).register(z.globalRegistry, {example: '1998-01-20'})

export const streetRegistrationNoRegex = '[1-9]\\d*'
export const streetOrientationNoRegex = `${streetRegistrationNoRegex}[A-Z]?`

export const streetNo = () => z.pipe(
  z.pipe(z.string(), z.transform((val) => val.toUpperCase().replace(/\s/g, ''))),
  z.string().check(
    z.trim(),
    z.minLength(1),
    z.maxLength(10),
    z.regex(new RegExp(
      `^(${streetRegistrationNoRegex}(\\/${streetOrientationNoRegex})?)$|^(${streetOrientationNoRegex})$`
    )),
  )
).register(z.globalRegistry, {example: '30'})

export const streetRegistrationNo = () => z.string().check(
  z.trim(),
  z.minLength(1),
  z.maxLength(10),
  z.regex(new RegExp(`^${streetRegistrationNoRegex}$`)),
).register(z.globalRegistry, {example: '1498'})

export const streetOrientationNo = () => z.pipe(
  z.pipe(z.string(), z.transform((val) => val.toUpperCase().replace(/\s/g, ''))),
  z.string().check(
    z.trim(),
    z.minLength(1),
    z.maxLength(10),
    z.regex(new RegExp(`^${streetOrientationNoRegex}$`)),
  )
).register(z.globalRegistry, {example: '30A'})

export const name = () => z.string().check(
  z.trim(),
  z.minLength(1),
  z.maxLength(50),
  z.regex(/^[^.,;]*$/),
).register(z.globalRegistry, {example: 'Peter'})

export const idNumber = () => z.string().check(
  z.trim(),
  z.regex(/^([A-Z]{2}\d{6,7}|[A-Z]\d{7}|\d{7})$/),
).register(z.globalRegistry, {example: 'AB123456'})

export const person = {
  legalForm: legalForm(),
  zipCode: standardString('82101'),
  city: standardString('Bratislava'),
  street: standardString('Tomášikova'),
  streetNo: streetNo(),
  streetRegistrationNo: streetRegistrationNo(),
  streetOrientationNo: streetOrientationNo(),
  birthday: birthday(),
  bornNumber: bornNumber(),
  firstName: name(),
  lastName: name(),
  idNumber: idNumber(),
  drivingLicenceYear: positiveInteger({max: getYear(new Date()), example: 2000}),
  ztp: z.optional(boolean()),
  companyNumber: companyNumber(),
  companyName: standardString('Férové PZP s. r. o.'),
}

// PZP

export const insurance = () => createEnum(ALL_INSURANCES)

export const damageClaimsCount = z.optional(positiveInteger())

export const paymentFrequency = () => createEnum(ALL_PAYMENT_FREQUENCIES)

export const beginDate = (
  {maxDays, maxMonths}: {maxDays?: number, maxMonths?: number} = {maxMonths: DEFAULT_BEGIN_DATE_MAX_MONTHS}
) => date().check(
  z.refine(
    (date) => isAfter(new Date(date), subDays(new Date(), 1)),
    {error: 'Dátum musí byť minimálne dnešný'},
  ),
  z.refine(
    (date) => !maxDays || isBefore(new Date(date), addDays(new Date(), maxDays)),
    {error: `Dátum môže byť maximálne ${maxDays} dní od dnes`},
  ),
  z.refine(
    (date) => !maxMonths || isBefore(new Date(date), addMonths(new Date(), maxMonths)),
    {error: `Dátum môže byť maximálne ${maxMonths} mesiacov od dnes`},
  ),
)

export const packagePrice = z.object({
  term: z.optional(z.number()),
  total: z.optional(z.number()),
})

export const packageOffer = z.object({
  insurance: insurance(),
  packageCoverage: z.string(),
  yearly: packagePrice,
  semiAnnually: packagePrice,
  quarterly: packagePrice,
})
export type PackageOffer = z.infer<typeof packageOffer>

export const vehicleIdentifiers = () => createEnum(ALL_VEHICLE_IDENTIFIERS)
